import { useMemoizedFn } from "ahooks";
import { useEffect, useRef, useState } from "react";

export function useInfiniteList<T>(
  queryFn: (page?: number) => Promise<T[]>,
  options?: {
    initialData: T[];
  }
) {
  const [list, setList] = useState<T[]>(options?.initialData || []);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const loadData = useCanceledFn(queryFn);

  const onLoadMore = useMemoizedFn(async () => {
    const res = await loadData(page);

    // 使用类型守卫判断是否是取消状态
    if ("canceled" in res) {
      return;
    }

    if (res.length > 0) {
      setList((prev) => prev.concat(res));
      setPage((prev) => prev + 1);
      setHasMore(true);
    } else {
      setHasMore(false);
    }
  });

  const resetData = useMemoizedFn(() => {
    setList([]);
    setPage(1);
    setHasMore(true);
    onLoadMore();
  });

  const hasInitData = useRef(options?.initialData?.length > 0);
  useEffect(() => {
    if (!hasInitData.current) {
      // 没有初始数据时，自动加载第一页数据
      onLoadMore();
    }
  }, [onLoadMore]);

  return {
    list,
    setList,
    hasMore,
    page,
    onLoadMore,
    resetData,
  };
}

export function useCanceledFn<T extends (...args: any[]) => Promise<any>>(
  fn: T
) {
  const countRef = useRef(0);

  return useMemoizedFn(
    async (
      ...args: Parameters<T>
    ): Promise<ReturnType<T> | { canceled: true }> => {
      countRef.current += 1;
      const currentCount = countRef.current;
      const result = await fn(...args);

      // prevent run.then when request is canceled
      if (currentCount !== countRef.current) {
        return {
          canceled: true,
        };
      }

      return result;
    }
  );
}
